import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import moment from 'moment';
import { catchError, flatMap, map, switchMap } from 'rxjs/operators';
import { UserAuthorization } from '.';
import * as fromShared from '../../shared';
import {
    DisplayMessageMinimum,
    IStatusMessage,
    ResponseStatus
} from '../../shared';
import { UserAuthorizationsService } from '../user-authorizations.service';
import * as UserAuthorizationActions from './user-authorization.actions';

@Injectable()
export class UserAuthorizationEffects {
    getUserAuthorizations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserAuthorizationActions.getUserAuthorizations),
            switchMap(action =>
                this.service.getUserAuthorizations({ ...action }).pipe(
                    flatMap(userAuthorizations => [
                        UserAuthorizationActions.loadUserAuthorizations({
                            userAuthorizations: userAuthorizations.map(
                                auth => ({
                                    ...auth,
                                    EmpAuthEndDate: moment(
                                        auth.EmpAuthEndDate
                                    ).toDate(),
                                    EmpAuthStartDate: moment(
                                        auth.EmpAuthStartDate
                                    ).toDate()
                                })
                            )
                            //  as UserAuthorization[]
                        }),
                        new fromShared.Errors.SetErrors(
                            [
                                ...userAuthorizations
                                    .filter(
                                        a =>
                                            a.Status.StatusCode >=
                                            DisplayMessageMinimum
                                    )
                                    .map(a => a.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        ),
                        UserAuthorizationActions.getPendingUserAuthorizationsCount()
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    putUserAuthorizations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserAuthorizationActions.putUserAuthorzations),
            switchMap(action =>
                this.service.putUserAuthorizations(action.authorizations).pipe(
                    flatMap(resp => [
                        UserAuthorizationActions.upsertUserAuthorizations({
                            userAuthorizations: resp.Items.map(auth => ({
                                ...auth,
                                EmpAuthEndDate: moment(
                                    auth.EmpAuthEndDate
                                ).toDate(),
                                EmpAuthStartDate: moment(
                                    auth.EmpAuthStartDate
                                ).toDate()
                            }))
                        }),
                        new fromShared.Errors.RemoveErrors(
                            resp.Items.filter(
                                x =>
                                    x.Status.StatusCode ===
                                    ResponseStatus.Success
                            ).map(x => x.EmpAuthID.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            fromShared.Errors.MapErrorsToStatus(resp)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    deleteUserAuthorizations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserAuthorizationActions.removeUserAuthorizations),
            switchMap(action =>
                this.service.deleteAuthorizations(action.ids).pipe(
                    flatMap(resp =>
                        fromShared.Errors.IsSuccessStatus(resp.StatusCode)
                            ? [
                                  UserAuthorizationActions.deleteUserAuthorizations(
                                      {
                                          ids: action.ids
                                      }
                                  ),
                                  new fromShared.Errors.SetErrors(
                                      [
                                          ...resp.ErrorMessages.map(
                                              x =>
                                                  ({
                                                      StatusCode: -1,
                                                      Message: x,
                                                      MsgProcessID: 0,
                                                      MsgEntityID: ''
                                                  } as IStatusMessage)
                                          ),
                                          ...resp.Items.filter(
                                              x =>
                                                  x &&
                                                  x.Status.StatusCode >=
                                                      DisplayMessageMinimum
                                          ).map(x => x.Status)
                                      ].filter(fromShared.Errors.filterErrors)
                                  )
                              ]
                            : [
                                  new fromShared.Errors.SetErrors(
                                      fromShared.Errors.MapErrorsToStatus(resp)
                                  ),
                                  UserAuthorizationActions.upsertUserAuthorizations(
                                      {
                                          userAuthorizations: resp.Items
                                      }
                                  )
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    getPendingAuthorizationsCount$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserAuthorizationActions.getPendingUserAuthorizationsCount),
            switchMap(() =>
                this.service.getPendingAuthorizationsCount().pipe(
                    flatMap(count => [
                        UserAuthorizationActions.loadPendingUserAuthorizationsCount(
                            {
                                count
                            }
                        ),
                        UserAuthorizationActions.getPendingUserAuthorizations()
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    getPendingAuthorizations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserAuthorizationActions.getPendingUserAuthorizations),
            switchMap(() =>
                this.service.getPendingAuthorizations().pipe(
                    map(auths =>
                        UserAuthorizationActions.loadPendingUserAuthorizations({
                            auths
                        })
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    addPendingAuthorizations$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(UserAuthorizationActions.addPendingUserAuthorizations),
            switchMap(action =>
                this.service.addPendingAuthorizations(action).pipe(
                    flatMap(userAuthorizations => [
                        UserAuthorizationActions.loadUserAuthorizations({
                            userAuthorizations:
                                userAuthorizations as UserAuthorization[]
                        }),
                        UserAuthorizationActions.getPendingUserAuthorizationsCount()
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });
    constructor(
        private actions$: Actions,
        private service: UserAuthorizationsService
    ) {}
}
